// Radha — auth layer.
// Holds the RS256 access token, exposes the decoded tenant/role claims, and a
// React hook for the app shell's auth gate. The token is obtained from the
// gateway's login endpoint; the backend verifies the RS256 signature and the
// tenant_id claim on every request.
//
// SECURITY NOTE: this stores a short-lived access token in memory + sessionStorage
// (cleared on tab close), NOT localStorage, to limit XSS exposure. Pair with a
// short token TTL and a refresh flow at the gateway. Never store long-lived
// credentials client-side.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "radha.access_token";

export interface Claims {
  tenant_id: string;
  user_id: string;
  role: string;
  exp: number; // unix seconds
}

// --- token storage (memory + sessionStorage mirror) -----------------------
let _token = "";

export function getToken(): string {
  if (_token) return _token;
  _token = sessionStorage.getItem(STORAGE_KEY) ?? "";
  return _token;
}

function setToken(token: string): void {
  _token = token;
  if (token) sessionStorage.setItem(STORAGE_KEY, token);
  else sessionStorage.removeItem(STORAGE_KEY);
  // legacy bridge for hooks that read window.__RADHA_TOKEN__
  (window as any).__RADHA_TOKEN__ = token;
}

// --- claim decode (no verification client-side; the server verifies) -------
export function decodeClaims(token: string): Claims | null {
  try {
    const payload = token.split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return {
      tenant_id: json.tenant_id,
      user_id: json.user_id ?? json.sub,
      role: json.role,
      exp: json.exp,
    };
  } catch {
    return null;
  }
}

function isExpired(claims: Claims | null): boolean {
  return !claims || claims.exp * 1000 <= Date.now();
}

// --- login / logout --------------------------------------------------------
// Exchanges credentials at the gateway for an access token. The gateway (not the
// services) owns authentication; we only receive and carry the token.
export async function login(email: string, password: string): Promise<Claims> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json?.error?.message || "login failed");
  }
  const token = json.data?.access_token;
  const claims = decodeClaims(token);
  if (!token || !claims) throw new Error("invalid token from server");
  setToken(token);
  return claims;
}

export function logout(): void {
  setToken("");
}

// --- React hook ------------------------------------------------------------
export function useAuth() {
  const [claims, setClaims] = useState<Claims | null>(() => decodeClaims(getToken()));

  useEffect(() => {
    if (isExpired(claims)) {
      setToken("");
      setClaims(null);
    }
  }, [claims]);

  const doLogin = useCallback(async (email: string, password: string) => {
    const c = await login(email, password);
    setClaims(c);
    return c;
  }, []);

  const doLogout = useCallback(() => {
    logout();
    setClaims(null);
  }, []);

  return {
    claims,
    isAuthenticated: !isExpired(claims),
    login: doLogin,
    logout: doLogout,
  };
}
